import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { RotatingLines } from "react-loader-spinner";
import Select from "react-select";
import { debounce } from "lodash";

export default function ClassMaster() {
    // State management
    const [state, setState] = useState({
        mcourses: [],
        classes: [],
        loading: true,
        formSubmitting: false,
        formData: {
            classId: 0,
            className: "",
            classDescription: "",
            classDetailStatus: true,
        },
        searchQuery: "",
        filterStatus: "all",
        currentPage: 0,
        itemsPerPage: 5,
    });

    // Helper function to update state
    const updateState = (newState) => {
        setState((prev) => ({ ...prev, ...newState }));
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchClasses();
        fetchMainCourses();
    }, []);

    // API calls
    const fetchMainCourses = async () => {
        try {
            const response = await axios.get(
                `${API_LoginURL}Course/CourseMasterList`,
                {
                    headers: getHeaders(),
                }
            );
            updateState({
                mcourses: Array.isArray(response.data) ? response.data : [],
            });
        } catch (error) {
            console.error("Error fetching courses:", error);
            Swal.fire("Error!", "Failed to load courses.", "error");
        }
    };

    const fetchClasses = async () => {
        updateState({ loading: true });
        try {
            const response = await axios.get(
                `${API_LoginURL}class/ClassMasterList`,
                {
                    headers: getHeaders(),
                }
            );
            updateState({
                classes: Array.isArray(response.data) ? response.data : [],
                loading: false,
            });
        } catch (error) {
            console.error("Error fetching classes:", error);
            Swal.fire("Error!", "Failed to load classes.", "error");
            updateState({ loading: false });
        }
    };

    // Helper function for headers
    const getHeaders = () => ({
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("token"),
    });

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        updateState({
            formData: {
                ...state.formData,
                [name]: value,
            },
        });
    };

    const handleSelectChange = (selectedOption) => {
        updateState({
            formData: {
                ...state.formData,
                courseId: selectedOption ? selectedOption.courseId : 0,
            },
        });
    };

    // Debounced search
    const debouncedSearch = useMemo(
        () =>
            debounce((query) => {
                updateState({ searchQuery: query, currentPage: 0 });
            }, 300),
        []
    );

    const handleSearchChange = (e) => {
        debouncedSearch(e.target.value);
    };

    const handleFilterChange = (e) => {
        updateState({
            filterStatus: e.target.value,
            currentPage: 0,
        });
    };

    const resetForm = () => {
        updateState({
            formData: {
                classId: 0,
                className: "",
                classDescription: "",
                classDetailStatus: true,
            },
        });
    };

    const handleEdit = (classItem) => {
        updateState({
            formData: {
                ...classItem,
                classDetailStatus: classItem.classDetailStatus !== false, // Ensure boolean
            },
        });
    };

    const handleDelete = async (classId) => {
        const confirmation = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (!confirmation.isConfirmed) return;

        try {
            await axios.post(
                `${API_LoginURL}class/DeleteClassDetails`,
                { classId },
                { headers: getHeaders() }
            );
            updateState({
                classes: state.classes.filter(
                    (item) => item.classId !== classId
                ),
            });
            Swal.fire("Deleted!", "Class has been deleted.", "success");
        } catch (error) {
            console.error("Error deleting class:", error);
            Swal.fire("Error", "Failed to delete class", "error");
        }
    };

    const handlePageChange = ({ selected }) => {
        updateState({ currentPage: selected });
    };

    const handleFormSubmit = async () => {
        updateState({ formSubmitting: true });
        try {
            const response = await axios.post(
                `${API_LoginURL}class/ClassAddEdit`,
                state.formData,
                { headers: getHeaders() }
            );

            Swal.fire({
                icon:
                    response.data.response === "SUCCESS" ? "success" : "error",
                title:
                    response.data.response === "SUCCESS" ? "Success" : "Error",
                text:
                    response.data.response === "SUCCESS"
                        ? "Class saved successfully"
                        : response.data.response,
            });

            if (response.data.response === "SUCCESS") {
                fetchClasses();
                document.getElementById("closeClassModal").click();
                resetForm();
            }
        } catch (error) {
            console.error("Error saving class:", error);
            Swal.fire("Error", "Failed to save class", "error");
        } finally {
            updateState({ formSubmitting: false });
        }
    };
    
    const handleFormSubmitIfEmpty = () => {
        if (
            state.formData.className === "" ||
            state.formData.className === null ||
            state.formData.classDescription === "" ||
            state.formData.classDescription === null
        ) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Fields May Not be Empty",
            });
        } else {
            handleFormSubmit();
        }
    };

    // Filter and pagination
    const filteredClasses = state.classes.filter((item) => {
        const matchesSearch =
            item.className
                .toLowerCase()
                .includes(state.searchQuery.toLowerCase()) ||
            item.classDescription
                .toLowerCase()
                .includes(state.searchQuery.toLowerCase()) ||
            (item.courseName &&
                item.courseName
                    .toLowerCase()
                    .includes(state.searchQuery.toLowerCase()));

        const matchesStatus =
            state.filterStatus === "all" ||
            (state.filterStatus === "active" && item.classDetailStatus) ||
            (state.filterStatus === "inactive" && !item.classDetailStatus);

        return matchesSearch && matchesStatus;
    });

    const pageCount = Math.ceil(filteredClasses.length / state.itemsPerPage);
    const displayedClasses = filteredClasses.slice(
        state.currentPage * state.itemsPerPage,
        (state.currentPage + 1) * state.itemsPerPage
    );

    // Get course name for display
    const getCourseName = (courseId) => {
        const course = state.mcourses.find((c) => c.courseId === courseId);
        return course ? course.courseName : "N/A";
    };

    return (
        <div className="container-scroller">
            <Navbar />
            <div className="container-fluid page-body-wrapper">
                <SASidebar />
                <div className="main-panel">
                    <div className="content-wrapper">
                        <div className="col-lg-12 grid-margin stretch-card">
                            <div className="card">
                                <div className="card-body">
                                    {/* Header Section */}
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                                        <h4 className="card-title mb-3 mb-md-0">
                                            Class Master
                                        </h4>

                                        {/* Search and Filter */}
                                        <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                                            {/* Search */}
                                            <div
                                                className="flex-grow-1 me-md-2 position-relative"
                                                style={{ maxWidth: "500px" }}
                                            >
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm ps-5 rounded-pill border-0"
                                                    placeholder="Search by Class Name or Description"
                                                    onChange={
                                                        handleSearchChange
                                                    }
                                                    style={{
                                                        height: "38px",
                                                        boxShadow:
                                                            "0 2px 8px rgba(0,0,0,0.08)",
                                                        transition:
                                                            "all 0.3s ease",
                                                        backgroundColor:
                                                            "#f8f9fa",
                                                    }}
                                                    onFocus={(e) => {
                                                        e.target.style.boxShadow =
                                                            "0 2px 12px rgba(0,0,0,0.15)";
                                                        e.target.style.backgroundColor =
                                                            "#fff";
                                                    }}
                                                    onBlur={(e) => {
                                                        e.target.style.boxShadow =
                                                            "0 2px 8px rgba(0,0,0,0.08)";
                                                        e.target.style.backgroundColor =
                                                            "#f8f9fa";
                                                    }}
                                                />
                                                <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                            </div>

                                            {/* Filter */}
                                            {/* <div className="d-flex align-items-center">
                                                <i className="fas fa-filter me-2 text-muted"></i>
                                                <select
                                                    className="form-select"
                                                    value={state.filterStatus}
                                                    onChange={
                                                        handleFilterChange
                                                    }
                                                    style={{
                                                        minWidth: "120px",
                                                    }}
                                                >
                                                    <option value="all">
                                                        All Status
                                                    </option>
                                                    <option value="inactive">
                                                        Active
                                                    </option>
                                                    <option value="active">
                                                        Inactive
                                                    </option>
                                                </select>
                                            </div> */}

                                            {/* Add Class Button */}
                                            <button
                                                className="btn btn-primary d-flex align-items-center"
                                                data-bs-toggle="modal"
                                                data-bs-target="#classModal"
                                                onClick={resetForm}
                                            >
                                                <i className="fas fa-plus me-2"></i>
                                                <span className="d-none d-md-inline">
                                                    Add Class
                                                </span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Class Table */}
                                    <div className="table-responsive">
                                        {state.loading ? (
                                            <div
                                                className="d-flex justify-content-center align-items-center"
                                                style={{ height: "200px" }}
                                            >
                                                <RotatingLines
                                                    color="#4e73df"
                                                    size={50}
                                                />
                                                <p className="mt-3 ms-3">
                                                    Loading classes...
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <table className="table table-hover table-striped">
                                                    <thead className="table-primary">
                                                        <tr>
                                                            <th width="10%">
                                                                ID
                                                            </th>
                                                            <th width="25%">
                                                                Class Name
                                                            </th>
                                                            <th width="25%">
                                                                Description
                                                            </th>
                                                            {/* <th width="10%">
                                                                Status
                                                            </th> */}
                                                            <th
                                                                width="10%"
                                                                className="text-end"
                                                            >
                                                                Actions
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {displayedClasses.length >
                                                        0 ? (
                                                            displayedClasses.map(
                                                                (item) => (
                                                                    <tr
                                                                        key={
                                                                            item.classId
                                                                        }
                                                                    >
                                                                        <td>
                                                                            {
                                                                                item.classId
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                item.className
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                item.classDescription
                                                                            }
                                                                        </td>
                                                                        {/* <td>
                                                                            <span
                                                                                style={{
                                                                                    color: item.classDetailStatus
                                                                                        ? "red"
                                                                                        : "green",
                                                                                }}
                                                                            >
                                                                                {item.classDetailStatus
                                                                                    ? "Inactive"
                                                                                    : "Active"}
                                                                            </span>
                                                                        </td> */}
                                                                        <td className="text-end">
                                                                            <div className="d-flex justify-content-end gap-2">
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    data-bs-toggle="modal"
                                                                                    data-bs-target="#classModal"
                                                                                    onClick={() =>
                                                                                        handleEdit(
                                                                                            item
                                                                                        )
                                                                                    }
                                                                                    title="Edit"
                                                                                >
                                                                                    <i className="fas fa-edit"></i>
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-danger"
                                                                                    onClick={() =>
                                                                                        handleDelete(
                                                                                            item.classId
                                                                                        )
                                                                                    }
                                                                                    title="Delete"
                                                                                >
                                                                                    <i className="fas fa-trash"></i>
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            )
                                                        ) : (
                                                            <tr>
                                                                <td
                                                                    colSpan="6"
                                                                    className="text-center py-4"
                                                                >
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <img
                                                                            src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                                                            alt="No classes"
                                                                            style={{
                                                                                opacity: 0.5,
                                                                                width: "60px",
                                                                            }}
                                                                        />
                                                                        <p className="mt-3 text-muted">
                                                                            No
                                                                            classes
                                                                            found
                                                                        </p>
                                                                        <button
                                                                            className="btn btn-sm btn-primary mt-2"
                                                                            data-bs-toggle="modal"
                                                                            data-bs-target="#classModal"
                                                                            onClick={
                                                                                resetForm
                                                                            }
                                                                        >
                                                                            Add
                                                                            New
                                                                            Class
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>

                                                {/* Pagination */}
                                                {displayedClasses.length >
                                                    0 && (
                                                    <ReactPaginate
                                                        previousLabel={
                                                            <i className="fas fa-chevron-left"></i>
                                                        }
                                                        nextLabel={
                                                            <i className="fas fa-chevron-right"></i>
                                                        }
                                                        pageCount={pageCount}
                                                        onPageChange={
                                                            handlePageChange
                                                        }
                                                        containerClassName={
                                                            "pagination justify-content-center mt-4"
                                                        }
                                                        pageClassName={
                                                            "page-item"
                                                        }
                                                        pageLinkClassName={
                                                            "page-link"
                                                        }
                                                        previousClassName={
                                                            "page-item"
                                                        }
                                                        previousLinkClassName={
                                                            "page-link"
                                                        }
                                                        nextClassName={
                                                            "page-item"
                                                        }
                                                        nextLinkClassName={
                                                            "page-link"
                                                        }
                                                        activeClassName={
                                                            "active"
                                                        }
                                                        breakLabel={"..."}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={5}
                                                        forcePage={
                                                            state.currentPage
                                                        }
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Class Modal */}
            <div
                className="modal fade"
                id="classModal"
                tabIndex="-1"
                aria-labelledby="classModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header bg-light">
                            <h5 className="modal-title" id="classModalLabel">
                                {state.formData.classId > 0
                                    ? "Edit Class"
                                    : "Add New Class"}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <form>
                                <div className="mb-1 row">
                                    <label
                                        htmlFor="className"
                                        className="form-label required"
                                    >
                                        Class Name
                                    </label>
                                    <div className="col-sm-9">
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="className"
                                        name="className"
                                        value={state.formData.className}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                </div>
                                <div className="mb-1 row">
                                    <label
                                        htmlFor="classDescription"
                                        className="form-label"
                                    >
                                        Description
                                    </label>
                                    <div className="col-sm-9">
                                    <textarea
                                        className="form-control"
                                        id="classDescription"
                                        name="classDescription"
                                        rows="1"
                                        value={state.formData.classDescription}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                </div>
                                {/* {state.formData.classId > 0 && (
                                    <div className="mb-3">
                                        <label
                                            htmlFor="classDetailStatus"
                                            className="form-label"
                                        >
                                            Status
                                        </label>
                                        <select
                                            className="form-select"
                                            id="classDetailStatus"
                                            name="classDetailStatus"
                                            value={
                                                state.formData.classDetailStatus
                                            }
                                            onChange={handleInputChange}
                                        >
                                            <option value={true}>Active</option>
                                            <option value={false}>
                                                Inactive
                                            </option>
                                        </select>
                                    </div>
                                )} */}
                            </form>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                id="closeClassModal"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleFormSubmitIfEmpty}
                                // onClick={handleFormSubmit}
                                disabled={state.formSubmitting}
                            >
                                {state.formSubmitting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Submitting...
                                    </>
                                ) : (
                                    "Submit"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
